import {
    AutocompleteInput,
    Breadcrumb,
    Create,
    DateInput,
    ReferenceInput,
    SelectInput,
    SimpleForm,
} from "@/components/admin";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ClipboardList, DollarSign, User } from "lucide-react";
import { Link } from "react-router";
import { required, useCreate } from "ra-core";
import { FileInput } from "@/components/admin/file-input";
import { AutoReferenceField } from "../documents/shared/AutoReferenceField";
import { FilteredContactInput } from "../misc/FilteredContactInput";
import { AttachmentField } from "../notes/AttachmentField";
import { ProductRequirements } from "./ProductRequirements";
import { StatusSelectInput } from "../documents/shared/StatusSelectInput";

export const EnquiryCreate = () => {
    const [create] = useCreate();
    return (
        <Create disableBreadcrumb title="Capture New Enquiry">
            <SimpleForm className="max-w-5xl mx-auto space-y-6">
                <Breadcrumb>
                    <Breadcrumb.Item><Link to="/enquiries">Enquiries</Link></Breadcrumb.Item>
                    <Breadcrumb.PageItem>Create</Breadcrumb.PageItem>
                </Breadcrumb>

                {/* Header Card */}
                <Card className="w-full shadow-sm">
                    <CardHeader className="pb-3 border-b bg-muted/50">
                        <div className="flex items-center gap-2">
                            <ClipboardList className="w-5 h-5 text-muted-foreground" />
                            <CardTitle className="text-lg font-semibold tracking-tight">Enquiry Details</CardTitle>
                        </div>
                    </CardHeader>
                    <CardContent className="pt-6">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <AutoReferenceField docType="enquiry" />
                            <SelectInput
                                source="source"
                                label="Lead Source"
                                validate={required()}
                                choices={[
                                    { id: 'website', name: 'Website' },
                                    { id: 'email', name: 'Email' },
                                    { id: 'phone', name: 'Phone / WhatsApp' },
                                    { id: 'referral', name: 'Referral' },
                                    { id: 'trade_show', name: 'Trade Show' },
                                    { id: 'cold_call', name: 'Cold Calling' },
                                ]}
                                className="w-full"
                            />
                            <StatusSelectInput
                                source="status"
                                label="Status"
                                defaultValue="new"
                                choices={[
                                    { id: 'new', name: 'New' },
                                    { id: 'in_discussion', name: 'In Discussion' },
                                    { id: 'closed', name: 'Closed' },
                                ]}
                                className="w-full"
                            />
                        </div>
                    </CardContent>
                </Card>

                {/* Customer Card */}
                <Card className="w-full shadow-sm">
                    <CardHeader className="pb-3 border-b bg-muted/50">
                        <div className="flex items-center gap-2">
                            <User className="w-5 h-5 text-muted-foreground" />
                            <CardTitle className="text-lg font-semibold tracking-tight">Customer Information</CardTitle>
                        </div>
                    </CardHeader>
                    <CardContent className="pt-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <ReferenceInput source="assigned_to" reference="sales">
                                <AutocompleteInput label="Assigned Sales Rep" className="w-full" optionText={(record: any) => record ? `${record.first_name} ${record.last_name}` : ''} />
                            </ReferenceInput>
                            {/* V-8: Initial Score (qualification_score) removed per client request */}
                        </div>
                        <div className="pt-4">
                            <FileInput
                                source="attachments"
                                label="Attachments (drawings, PDF, images)"
                                multiple
                                accept={{
                                    "application/pdf": [".pdf"],
                                    "image/*": [".png", ".jpg", ".jpeg"],
                                    "application/msword": [".doc"],
                                    "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"],
                                    "application/vnd.ms-excel": [".xls"],
                                    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
                                }}
                            >
                                <AttachmentField source="src" title="title" target="_blank" />
                            </FileInput>
                        </div>
                    </CardContent>
                </Card>

                {/* Requirements Card (E-3 / V-9: Budget removed per client request) */}
                <Card className="w-full shadow-sm">
                    <CardHeader className="pb-3 border-b">
                        <div className="flex items-center gap-2">
                            <DollarSign className="w-5 h-5 text-muted-foreground" />
                            <CardTitle className="text-lg font-semibold tracking-tight">Requirements</CardTitle>
                        </div>
                    </CardHeader>
                    <CardContent className="pt-6 space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <DateInput source="requirements.timeline" label="Expected Timeline" className="w-full" />
                            <ProductRequirements />
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <ReferenceInput source="company_id" reference="companies">
                                <AutocompleteInput
                                    label="Company"
                                    className="w-full"
                                    onCreate={async (value?: string) => {
                                        const name = value?.trim();
                                        if (!name) return undefined;
                                        const { data } = await create(
                                            "companies",
                                            { data: { name } },
                                            { returnPromise: true },
                                        );
                                        return data;
                                    }}
                                />
                            </ReferenceInput>
                            <FilteredContactInput label="Contact Person" className="w-full" allowCreate />
                            <ReferenceInput source="ops_head_id" reference="sales">
                                <AutocompleteInput label="Assigned Ops Head" className="w-full" optionText={(record: any) => record ? `${record.first_name} ${record.last_name}` : ''} />
                            </ReferenceInput>
                            <ReferenceInput source="ops_team_member_id" reference="sales">
                                <AutocompleteInput label="Assigned Ops Team Member" className="w-full" optionText={(record: any) => record ? `${record.first_name} ${record.last_name}` : ''} />
                            </ReferenceInput>
                        </div>
                    </CardContent>
                </Card>
            </SimpleForm>
        </Create>
    );
};
